/**
 * Admin Panel - Vault Pool Management API
 *
 * Zod schemas for credential pool management endpoints.
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { ROUTE_SENSITIVITY } from "../../route-sensitivity";

// ============================================================
// Schemas
// ============================================================

/**
 * Organization info displayed in vault pool slots
 */
export const VaultPoolSlotOrganizationSchema = z.object({
  // Clerk org id (`org_2abc…`), not a UUID: `Organization.id` is `String @id`
  // with no `@db.Uuid`, and the handler selects it straight off that column.
  id: z.string().min(1),
  name: z.string(),
  logoUrl: z.string().url().optional()
});

/**
 * Vault pool slot (credential allocation)
 */
export const VaultPoolSlotSchema = z.object({
  // A cuid, not a UUID: `VaultPoolSlot.id` is `@default(cuid())` and
  // `createPoolSlot` never supplies one, so Prisma owns the format. `.uuid()`
  // rejected EVERY slot — assigned or not — in the generated client's
  // `Response.parse`, so the pool table broke as soon as the pool was non-empty.
  id: z.string().min(1),
  provider: z.string(),
  providerDisplayName: z.string(),
  displayName: z.string().nullable(),
  isAssigned: z.boolean(),
  // Also a Clerk org id — `VaultPoolSlot.assignedToOrg` is `String?`. Declaring
  // it `.uuid()` made the generated client's `Response.parse` throw for every
  // ASSIGNED slot, so the pool table broke as soon as a slot was in use.
  assignedToOrg: z.string().min(1).nullable(),
  assignedAt: z.string().nullable(),
  createdAt: z.string(),
  organization: VaultPoolSlotOrganizationSchema.optional()
});

/**
 * Vault pool statistics
 */
export const VaultPoolStatsSchema = z.object({
  byProvider: z.array(
    z.object({
      provider: z.string(),
      displayName: z.string(),
      total: z.number(),
      available: z.number(),
      assigned: z.number()
    })
  ),
  totals: z.object({
    total: z.number(),
    available: z.number(),
    assigned: z.number()
  })
});

/**
 * Add vault slot request body
 *
 * `secrets` is REQUIRED, because `VaultPoolService.addPoolSlot` cannot run
 * without it: every provider declares a non-empty `requiredSecrets`, so the
 * validation loop dereferences the record on its first iteration. Until
 * NEX-2974 the schema omitted the field entirely, which made the generated
 * client's body type omit it too — and `VaultPage.handleAddSlot` duly named
 * the parameter `_secrets` and sent `{ provider, displayName }`. The admin
 * "Add pool slot" button 500'd for every provider, and typecheck was happy at
 * both ends because neither end had anything to compare.
 *
 * The value never reaches an audit row in the clear: `secrets` is a
 * `SENSITIVE_KEY_STEMS` entry, so `maskSensitiveFields` replaces it with
 * `[REDACTED]` before any `extractMeta` sees it. Declaring it here does not
 * widen what gets stored.
 */
export const AddVaultSlotBodySchema = z.object({
  provider: z.string(),
  secrets: z.record(z.string(), z.string()),
  displayName: z.string().nullable().optional()
});

/**
 * Update vault slot request body
 */
export const UpdateVaultSlotBodySchema = z.object({
  displayName: z.string().nullable().optional(),
  /**
   * Rotated credentials, keyed by the provider's required field names.
   *
   * `VaultPoolService.updatePoolSlot` has always accepted these and re-encrypts
   * when present; the schema simply never said so, and the audit extractor's
   * "were secrets rotated?" field had to cast the body to a local DTO to see it.
   * The value is `[REDACTED]` by the time any extractor reads it — `secrets` is
   * a `SENSITIVE_KEY_STEMS` entry — so test it with `wasProvided`, never read it.
   *
   * Optional here and REQUIRED on `AddVaultSlotBodySchema`, because the two
   * services differ: `updatePoolSlot` re-encrypts only when secrets are present
   * and leaves the stored payload alone otherwise, while `addPoolSlot` has
   * nothing to store without them.
   */
  secrets: z.record(z.string(), z.string()).optional()
});

/**
 * Force release vault slot request body
 */
export const ForceReleaseVaultSlotBodySchema = z.object({
  reason: z.string().min(1, "Reason is required")
});

/**
 * Path vars for the per-slot vault routes.
 *
 * The key is `id` because that is the segment `AdminPanelVaultController` routes
 * on (`@Patch("pool/:id")`). A path variable's name is part of the route
 * identity: the admin tool catalog joins reflected routes to contracts on the
 * literal `METHOD path`, and `buildAdminRequest` fills `:params` from the tool's
 * arguments by name. `:slotId` described a route the server does not serve.
 */
export const VaultSlotPathVarsSchema = z.object({
  // Same cuid as `VaultPoolSlotSchema.id` — it is the same column. Nothing
  // parses this at runtime today, but `mergedInputSchema` folds PathVars into
  // the admin agent's tool input and `z.toJSONSchema` emits the `uuid` format
  // to the model, so the wrong fact was being taught even where it was inert.
  id: z.string().min(1)
});

/**
 * Path vars for listing a provider's slots — `@Get("pool/:provider")`.
 *
 * This was a QUERY param on `pool/by-provider`, a route the backend has never
 * served. The route that exists takes the provider as a path segment, so the
 * contract now describes that one.
 */
export const GetSlotsByProviderPathVarsSchema = z.object({
  provider: z.string()
});

/**
 * Vault slot details (extended info)
 */
export const VaultSlotDetailsSchema = VaultPoolSlotSchema.extend({
  /**
   * The slot's DECRYPTED secrets, keyed by credential field.
   *
   * This said `credentials: { encryptedData, lastRotated }` and no such field
   * has ever been returned — `VaultPoolService.getPoolSlotDetails` decrypts and
   * returns `secrets`. The response IS parsed by the generated client, so the
   * fictional key simply arrived `undefined`: the two modals worked around it
   * with `data as VaultPoolSlot & { secrets: … }` casts, and VaultPage's
   * "Copy API key" read `details.credentials?.encryptedData`, found nothing, and
   * left its toast stuck on "loading" forever.
   */
  secrets: z.record(z.string(), z.string())
});

/**
 * Force release response
 *
 * Only `message`. This declared `success` and `slot` as well, and the handler
 * has never returned either — it ends `createApiSuccess({ message })`.
 *
 * That was invisible while the contract pointed at `pool/:slotId/force-release`,
 * a route the server does not serve: the call 404'd before anything could parse.
 * Correcting the path makes the request land, which makes the generated client's
 * `Response.parse` the next thing to fail — and it would fail AFTER the slot was
 * already released, showing the operator an error for an action that succeeded
 * and leaving the table stale. Two lies on one route, where the first was hiding
 * the second.
 */
export const ForceReleaseResponseSchema = z.object({
  message: z.string()
});

// ============================================================
// ZAdminVault - Endpoint Definitions
// ============================================================

export const ZAdminVault = {
  /**
   * Get vault pool statistics by provider
   * GET /api/admin-panel/vault/pool/stats
   */
  GetPoolStats: {
    method: "GET" as const,
    path: "/api/admin-panel/vault/pool/stats",
    Response: ApiSuccessSchema(VaultPoolStatsSchema)
  },

  /**
   * Get all vault pool slots
   * GET /api/admin-panel/vault/pool
   */
  GetPoolSlots: {
    method: "GET" as const,
    path: "/api/admin-panel/vault/pool",
    Response: ApiSuccessSchema(z.array(VaultPoolSlotSchema))
  },

  /**
   * Get vault pool slots filtered by provider
   * GET /api/admin-panel/vault/pool/:provider
   */
  GetSlotsByProvider: {
    method: "GET" as const,
    path: "/api/admin-panel/vault/pool/:provider",
    PathVars: GetSlotsByProviderPathVarsSchema,
    Response: ApiSuccessSchema(z.array(VaultPoolSlotSchema))
  },

  /**
   * Add a new vault pool slot
   * POST /api/admin-panel/vault/pool
   */
  AddSlot: {
    method: "POST" as const,
    path: "/api/admin-panel/vault/pool",
    Body: AddVaultSlotBodySchema,
    Response: ApiSuccessSchema(VaultPoolSlotSchema)
  },

  /**
   * Update a vault pool slot
   * PATCH /api/admin-panel/vault/pool/:id
   */
  UpdateSlot: {
    method: "PATCH" as const,
    path: "/api/admin-panel/vault/pool/:id",
    PathVars: VaultSlotPathVarsSchema,
    Body: UpdateVaultSlotBodySchema,
    Response: ApiSuccessSchema(VaultPoolSlotSchema)
  },

  /**
   * Remove a vault pool slot
   * DELETE /api/admin-panel/vault/pool/:id
   */
  RemoveSlot: {
    method: "DELETE" as const,
    path: "/api/admin-panel/vault/pool/:id",
    PathVars: VaultSlotPathVarsSchema,
    // `{ message }`, not `void`. The handler has always returned one, and the
    // route carried `@HttpCode(204)` — which forbids a body, so the client got
    // an empty response and `ApiSuccessSchema(z.void()).parse("")` threw. The
    // slot really was removed; the panel just reported a failure. The 204 is
    // gone and the contract now says what comes back.
    Response: ApiSuccessSchema(z.object({ message: z.string() }))
  },

  /**
   * Force release a vault pool slot from an organization
   * POST /api/admin-panel/vault/pool/:id/release
   */
  ForceReleaseSlot: {
    method: "POST" as const,
    path: "/api/admin-panel/vault/pool/:id/release",
    PathVars: VaultSlotPathVarsSchema,
    Body: ForceReleaseVaultSlotBodySchema,
    Response: ApiSuccessSchema(ForceReleaseResponseSchema)
  },

  /**
   * Get detailed information about a vault pool slot
   * GET /api/admin-panel/vault/pool/:id/details
   */
  GetSlotDetails: {
    method: "GET" as const,
    path: "/api/admin-panel/vault/pool/:id/details",
    // `VaultSlotDetailsSchema.secrets` is the slot's DECRYPTED credentials —
    // plaintext, straight out of `getPoolSlotDetails`, which logs a warning on
    // every call for exactly that reason. The panel keeps the endpoint (it
    // renders and rotates these values); only the tool projection is refused.
    sensitivity: ROUTE_SENSITIVITY.SECRET,
    PathVars: VaultSlotPathVarsSchema,
    Response: ApiSuccessSchema(VaultSlotDetailsSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type VaultPoolSlotOrganization = z.infer<typeof VaultPoolSlotOrganizationSchema>;
export type VaultPoolSlot = z.infer<typeof VaultPoolSlotSchema>;
export type VaultPoolStats = z.infer<typeof VaultPoolStatsSchema>;
export type VaultSlotDetails = z.infer<typeof VaultSlotDetailsSchema>;
